import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Phone, MapPin, Clock, Car, Loader2 } from "lucide-react";

import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { DISPATCH_PHONE_DISPLAY, DISPATCH_TEL_HREF } from "@/lib/contact";

function fmtDt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "long", month: "long", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-3 border-b border-white/10 last:border-b-0">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6]">{label}</div>
      <div className="text-sm text-white text-right max-w-[60%] break-words">{value}</div>
    </div>
  );
}

function FareTable({ quote }) {
  if (!quote) return null;
  return (
    <table className="w-full mt-3" data-testid="manage-fare-table">
      <tbody>
        {(quote.breakdown || []).map((b, i) => {
          const isTotal = b.label === "Total";
          return (
            <tr key={i} className={isTotal ? "border-t border-white/20" : ""}>
              <td className={`py-2 text-sm ${isTotal ? "text-white font-medium" : "text-[#8E9AA6]"}`}>{b.label}</td>
              <td className={`py-2 text-right text-sm tabular-nums ${isTotal ? "text-white font-medium" : "text-[#E6E6E6]"}`}>${b.amount.toFixed(2)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function ManageBooking() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const token = search.get("token") || "";

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Phase 16e Bug 1 fix — hard-reset local state on every effect run so a
    // stale `error` (from React 18 StrictMode's double-invocation or a fast
    // remount) can't linger and cause the not-found panel to render on top
    // of a successful response. `booking` presence is the single source of
    // truth for success rendering.
    let alive = true;
    setBooking(null);
    setError("");
    setLoading(true);

    (async () => {
      if (!token) {
        if (alive) { setError("MISSING_TOKEN"); setLoading(false); }
        return;
      }
      try {
        const { data } = await api.get(`/bookings/guest/${id}`, { params: { token } });
        if (alive) {
          setBooking(data);
          setError("");  // clear any transient error that races in first
          console.log("[manage-booking] loaded", data.id, "status:", data.status);
        }
      } catch (e) {
        if (alive) setError(e?.response?.status === 404 ? "NOT_FOUND" : formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, token]);

  if (loading) {
    return (
      <Layout>
        <section className="min-h-screen pt-32 pb-24 flex items-center justify-center">
          <div data-testid="manage-loading" className="flex items-center gap-3 text-[#8E9AA6] text-sm tracking-widest uppercase">
            <Loader2 size={18} className="animate-spin" /> Loading reservation…
          </div>
        </section>
      </Layout>
    );
  }

  // Booking presence wins over any transient error state — matches the API
  // contract: 200 → booking exists → show it. Only render not-found when we
  // truly have no booking after the fetch settled.
  if (!booking) {
    return (
      <Layout>
        <section className="min-h-screen pt-32 pb-24 flex items-center justify-center px-6">
          <div data-testid="manage-not-found" className="max-w-lg text-center backdrop-blur-xl bg-[#121214]/70 border border-white/15 px-8 py-10">
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-4">
              Reservation Not Found
            </div>
            <h1 className="font-serif text-3xl text-white font-semibold mb-4">
              We couldn&apos;t locate this reservation.
            </h1>
            <p className="text-sm text-[#8E9AA6] leading-relaxed mb-6">
              Please double-check the link. If you continue to see this message,
              our 24/7 dispatch can pull up your booking manually.
            </p>
            <a
              href={DISPATCH_TEL_HREF}
              data-testid="manage-not-found-dispatch-link"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-[#E6E6E6] px-8 py-4 font-medium text-xs tracking-[0.25em] uppercase"
            >
              <Phone size={14} /> Call Dispatch {DISPATCH_PHONE_DISPLAY}
            </a>
          </div>
        </section>
      </Layout>
    );
  }

  const q = booking.quote || {};

  return (
    <Layout>
      <section
        data-testid="manage-booking-page"
        className="min-h-screen pt-32 pb-24 relative overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/booking-bg.jpg')" }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-[#0B0B0C]/92 backdrop-blur-sm" aria-hidden />

        <div className="relative max-w-3xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle2 size={20} className="text-white" />
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6]">
              Reservation · Status: <span data-testid="manage-status" className="text-white">{booking.status.replace("_", " ")}</span>
            </div>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-white font-semibold tracking-tighter">
            Your reservation
          </h1>
          <div data-testid="manage-booking-id" className="mt-3 font-mono text-xs text-[#8E9AA6] break-all">
            {booking.id}
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="backdrop-blur-xl bg-[#121214]/70 border border-white/15 px-6 py-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-3">Trip Details</div>
              <Row label={<><MapPin size={11} className="inline mr-1" /> Pickup</>} value={booking.pickup_address} />
              {booking.dropoff_address && <Row label={<><MapPin size={11} className="inline mr-1" /> Drop-off</>} value={booking.dropoff_address} />}
              <Row label={<><Clock size={11} className="inline mr-1" /> When</>} value={fmtDt(booking.pickup_datetime)} />
              <Row label={<><Car size={11} className="inline mr-1" /> Vehicle</>} value={booking.vehicle_category.replace("_", " ")} />
              <Row label="Passengers" value={booking.passengers} />
              <Row label="Luggage" value={booking.luggage} />
              {booking.flight_number && <Row label="Flight" value={booking.flight_number} />}
            </div>

            <div className="backdrop-blur-xl bg-[#121214]/70 border border-white/15 px-6 py-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-3">Fare</div>
              <FareTable quote={q} />
              <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6]">Payment</div>
              <div className="text-sm text-white mt-1" data-testid="manage-payment-status">{booking.payment_status || "Awaiting Payment"}</div>
            </div>
          </div>

          <div className="mt-8 backdrop-blur-xl bg-[#121214]/70 border border-white/15 px-6 py-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-3">Need to make a change?</div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={DISPATCH_TEL_HREF}
                data-testid="manage-dispatch-link"
                className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6E6E6] px-6 py-3 font-medium text-xs tracking-[0.25em] uppercase"
              >
                <Phone size={13} /> Call Dispatch {DISPATCH_PHONE_DISPLAY}
              </a>
              <Link
                to="/book"
                data-testid="manage-book-again-link"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white hover:bg-white hover:text-black px-6 py-3 font-medium text-xs tracking-[0.25em] uppercase"
              >
                Book Another Trip
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
